import { IPaginationMeta, IPaginationSummary } from "../interfaces";
import { DISPLAY_CONSTANTS } from "../constants";

/**
 * Checks if a value is null or undefined
 * @param value - The value to check
 * @returns True if the value is null or undefined, false otherwise
 */
export const isNullOrUndefined = <T>(
  value: T | null | undefined
): value is null | undefined => {
  return value === null || value === undefined;
};

/**
 * Checks if a value is defined (not null and not undefined)
 * @param value - The value to check
 * @returns True if the value is defined, false otherwise
 */
export const isDefined = <T>(value: T | null | undefined): value is T => {
  return !isNullOrUndefined(value);
};

/**
 * Safely checks if a property on an object is defined
 * @param obj - The object to check
 * @param key - The property key to check
 * @returns True if the property is defined, false otherwise
 */
export const isPropertyDefined = <T extends object, K extends keyof T>(
  obj: T | null | undefined,
  key: K
): boolean => {
  return !isNullOrUndefined(obj) && !isNullOrUndefined(obj[key]);
};

/**
 * Checks if a value is empty (null, undefined, empty string, empty array, or empty object)
 * @param value - The value to check
 * @returns True if the value is empty, false otherwise
 */
export const isEmpty = (value: unknown): boolean => {
  if (isNullOrUndefined(value)) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object" && value !== null)
    return Object.keys(value).length === 0;
  return false;
};

/**
 * Checks if a property exists on an object regardless of its value
 * @param obj - The object to check
 * @param prop - The property name as a string
 * @returns True if the property exists on the object, false otherwise
 * @example
 * const hasName = hasProperty(user, 'name');
 */
export const hasProperty = <T extends object>(
  obj: T,
  prop: string
): boolean => {
  return prop in obj;
};

/**
 * Checks if a key is present in an object (handles null/undefined safely)
 * @param obj - The object to check (can be null or undefined)
 * @param key - The key to check for
 * @returns True if the object exists and has the key, false otherwise
 * @example
 * const hasName = hasKey(user, 'name');
 */
export const hasKey = <T extends object>(
  obj: T | null | undefined,
  key: string
): boolean => {
  return isDefined(obj) && typeof obj === "object" && key in obj;
};

/**
 * Checks if an object has all of the specified keys (handles null/undefined safely)
 * @param obj - The object to check (can be null or undefined)
 * @param keys - Array of keys to check for
 * @returns True if the object exists and has all the keys, false otherwise
 * @example
 * const hasAllBounds = hasKeys(bounds, ['north', 'south', 'east', 'west']);
 */
export const hasKeys = <T extends object>(
  obj: T | null | undefined,
  keys: string[]
): boolean => {
  if (!isDefined(obj) || typeof obj !== "object") return false;
  return keys.every((key) => key in obj);
};


/**
 * Checks if a value is defined and not empty
 * @param value - The value to check
 * @returns True if the value is defined and not empty, false otherwise
 * @example
 * if (isDefinedAndNotEmpty(params.cityCode)) { ... }
 */
export const isDefinedAndNotEmpty = (value: unknown): boolean => {
  return isDefined(value) && !isEmpty(value);
};

/**
 * Appends a query parameter to URLSearchParams if the value is defined and not empty
 * @param queryParams - The URLSearchParams instance to append to
 * @param key - The parameter key
 * @param value - The parameter value (will be converted to string)
 * @example
 * appendQueryParam(queryParams, 'cityCode', params.cityCode);
 */
export const appendQueryParam = (
  queryParams: URLSearchParams,
  key: string,
  value: unknown
): void => {
  if (isDefinedAndNotEmpty(value)) {
    queryParams.append(key, String(value));
  }
};

/**
 * Type-safe way to handle unknown data structures
 * Use instead of casting to 'any' type
 * @param value - The value to type safely access
 * @param defaultValue - The default value to return if value is undefined or null
 * @returns The value cast to type T or the default value
 * @example
 * const userData = safelyAccess<UserData>(apiResponse, {} as UserData);
 */
export const safelyAccess = <T>(
  value: T | null | undefined,
  defaultValue: T
): T => {
  return isNullOrUndefined(value) ? defaultValue : value;
};

/**
 * Safely parses a string or number to integer
 * @param value - The value to parse
 * @param defaultValue - The default value to return if parsing fails
 * @returns The parsed integer or default value
 */
export const safeParseInt = (
  value: string | number,
  defaultValue = 0
): number => {
  if (typeof value === "number")
    return isValidNumber(value) ? Math.floor(value) : defaultValue;
  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    return isValidNumber(parsed) ? parsed : defaultValue;
  }
  return defaultValue;
};

/**
 * Checks if a value is a valid number (not NaN)
 * @param value - The value to check
 * @returns True if the value is a valid number, false otherwise
 */
export const isValidNumber = (value: unknown): value is number => {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
};

/**
 * Calculate the first and last item indices for a paginated response
 * @param meta - Pagination metadata received from the backend
 * @returns Object containing the first and last item indices (1-based) and total count
 */
export const getPaginationSummary = (
  meta: IPaginationMeta
): IPaginationSummary => {
  const totalCount = Math.max(0, meta.totalCount ?? 0);
  const pageSize = Math.max(0, meta.limit ?? meta.pageSize ?? 0);

  if (totalCount === 0 || pageSize === 0) {
    return {
      firstItem: 0,
      lastItem: 0,
      totalCount,
    };
  }

  const totalPages = Math.max(1, meta.totalPages ?? 1);
  const currentPage = Math.min(Math.max(1, meta.currentPage ?? 1), totalPages);
  const firstItem = (currentPage - 1) * pageSize + 1;
  const lastItem = Math.min(firstItem + pageSize - 1, totalCount);

  return {
    firstItem,
    lastItem,
    totalCount,
  };
};

/**
 * Converts an enum value to a human-readable string
 * Keeps only the first letter uppercase, converts rest to lowercase
 * Handles underscores by replacing them with spaces
 * @param enumValue - The enum value to convert (e.g., "WALKING", "ONE_TIME")
 * @returns Human-readable string (e.g., "Walking", "One Time")
 * @example
 * enumToHumanReadable("WALKING") // "Walking"
 * enumToHumanReadable("ONE_TIME") // "One Time"
 * enumToHumanReadable("SUBSCRIPTION") // "Subscription"
 */
export const enumToHumanReadable = (enumValue?: string | null): string => {
  if (isNullOrUndefined(enumValue)) return DISPLAY_CONSTANTS.NOT_AVAILABLE;
  if (isEmpty(enumValue)) return enumValue;

  // Replace underscores with spaces
  const withSpaces = enumValue.replace(/_/g, " ");

  // Split by spaces, capitalize first letter of each word, lowercase the rest
  return withSpaces
    .split(" ")
    .map((word) => {
      if (isEmpty(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

/**
 * Truncates a string and appends ellipsis if needed.
 * @param value - Input string to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string with ellipsis
 */
export const truncateText = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}...`;
};

/**
 * Type guard to check if a value is a valid enum value
 * @param value - The value to check
 * @param enumObj - The enum object
 * @returns True if the value is a valid enum value
 */
export const isEnumValue = <T extends Record<string, unknown>>(
  value: unknown,
  enumObj: T
): value is T[keyof T] => {
  const enumValues = Object.values(enumObj);
  return enumValues.includes(value);
};

/**
 * Safely gets a property value from an object without type assertions
 * @param obj - The object to get the property from
 * @param key - The property key to retrieve
 * @returns The property value or undefined if not found
 */
export const getPropertyValue = (obj: unknown, key: string): unknown => {
  if (typeof obj !== "object" || obj === null) return undefined;
  const descriptor = Object.getOwnPropertyDescriptor(obj, key);
  return descriptor?.value;
};
